import { useEffect, useMemo, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { Delete, X } from 'lucide-react'
import { evaluateExpression } from '@shared/math'
import { useStore } from '../../store'
import { cn } from '../../lib/utils'

type Key = {
  label: string
  insert?: string
  action?: 'clear' | 'back' | 'eq' | 'ans'
  variant?: 'fn' | 'op' | 'eq' | 'num'
}

const FN_KEYS: Key[] = [
  { label: 'sin', insert: 'sin(', variant: 'fn' },
  { label: 'cos', insert: 'cos(', variant: 'fn' },
  { label: 'tan', insert: 'tan(', variant: 'fn' },
  { label: 'ln', insert: 'log(', variant: 'fn' },
  { label: 'log', insert: 'log10(', variant: 'fn' },
  { label: '√', insert: 'sqrt(', variant: 'fn' },
  { label: 'xʸ', insert: '^', variant: 'fn' },
  { label: 'n!', insert: '!', variant: 'fn' },
  { label: 'π', insert: 'pi', variant: 'fn' },
  { label: 'e', insert: 'e', variant: 'fn' },
]

const PAD_KEYS: Key[] = [
  { label: 'C', action: 'clear', variant: 'op' },
  { label: '⌫', action: 'back', variant: 'op' },
  { label: '(', insert: '(', variant: 'op' },
  { label: ')', insert: ')', variant: 'op' },
  { label: '7', insert: '7' },
  { label: '8', insert: '8' },
  { label: '9', insert: '9' },
  { label: '÷', insert: '/', variant: 'op' },
  { label: '4', insert: '4' },
  { label: '5', insert: '5' },
  { label: '6', insert: '6' },
  { label: '×', insert: '*', variant: 'op' },
  { label: '1', insert: '1' },
  { label: '2', insert: '2' },
  { label: '3', insert: '3' },
  { label: '−', insert: '-', variant: 'op' },
  { label: '0', insert: '0' },
  { label: '.', insert: '.' },
  { label: 'Ans', action: 'ans', variant: 'op' },
  { label: '+', insert: '+', variant: 'op' },
]

export function Calculator() {
  const open = useStore((s) => s.calculatorOpen)
  const toggle = useStore((s) => s.toggleCalculator)
  const seed = useStore((s) => s.calcSeed)
  const [expr, setExpr] = useState('')
  const [ans, setAns] = useState('0')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (seed != null) {
      setExpr(seed)
      useStore.setState({ calcSeed: null })
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }, [seed])

  const preview = useMemo(() => {
    if (!expr.trim()) return { ok: true, value: '' }
    try {
      return { ok: true, value: evaluateExpression(expr).result }
    } catch {
      return { ok: false, value: '' }
    }
  }, [expr])

  const press = (k: Key) => {
    if (k.action === 'clear') return setExpr('')
    if (k.action === 'back') return setExpr((e) => e.slice(0, -1))
    if (k.action === 'ans') return setExpr((e) => e + ans)
    if (k.action === 'eq') {
      try {
        const r = evaluateExpression(expr).result
        setAns(r)
        setExpr(r)
      } catch {
        /* leave as-is on error */
      }
      return
    }
    if (k.insert) setExpr((e) => e + k.insert)
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.98 }}
          transition={{ type: 'spring', stiffness: 320, damping: 30 }}
          className="glass fixed bottom-4 right-4 z-40 w-[312px] max-w-[calc(100vw-2rem)] overflow-hidden shadow-[var(--e-3)]"
          style={{ borderRadius: 'var(--radius-lg)' }}
        >
          <div className="flex items-center justify-between border-b border-[var(--line-soft)] px-3.5 py-2.5">
            <span className="text-[12px] font-medium tracking-wide text-fg-muted">
              Scientific Calculator
            </span>
            <button
              onClick={() => toggle(false)}
              className="rounded-md p-1 text-fg-subtle hover:text-fg"
              title="Close"
            >
              <X size={15} />
            </button>
          </div>

          <div className="px-3.5 pb-2 pt-3">
            <input
              ref={inputRef}
              value={expr}
              onChange={(e) => setExpr(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') press({ label: '=', action: 'eq' })
              }}
              placeholder="0"
              inputMode="decimal"
              spellCheck={false}
              className="w-full bg-transparent text-right text-[20px] text-fg placeholder:text-fg-faint focus:outline-none"
            />
            <div
              className={cn(
                'num mt-0.5 h-5 text-right text-[13px]',
                preview.ok ? 'text-fg-subtle' : 'text-down',
              )}
            >
              {expr.trim() ? (preview.ok ? `= ${preview.value}` : 'error') : ''}
            </div>
          </div>

          <div className="grid grid-cols-5 gap-1 px-3 pb-2">
            {FN_KEYS.map((k) => (
              <CalcBtn key={k.label} k={k} onPress={press} />
            ))}
          </div>

          <div className="grid grid-cols-4 gap-1 px-3 pb-2">
            {PAD_KEYS.map((k) => (
              <CalcBtn key={k.label} k={k} onPress={press} />
            ))}
          </div>

          <div className="px-3 pb-3">
            <button
              onClick={() => press({ label: '=', action: 'eq' })}
              className="h-11 w-full rounded-xl bg-[var(--color-accent)] text-[16px] font-semibold text-[var(--color-base)] transition-opacity hover:opacity-90"
            >
              =
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function CalcBtn({ k, onPress }: { k: Key; onPress: (k: Key) => void }) {
  return (
    <button
      onClick={() => onPress(k)}
      className={cn(
        'flex h-10 items-center justify-center rounded-xl text-[14px] transition-colors',
        k.variant === 'fn' && 'bg-[var(--color-surface-1)] text-fg-muted hover:bg-[var(--color-surface-3)] text-[13px]',
        k.variant === 'op' && 'bg-[var(--color-surface-1)] text-fg hover:bg-[var(--color-surface-3)]',
        (!k.variant || k.variant === 'num') &&
          'bg-[var(--color-surface-2)] text-fg hover:bg-[var(--color-surface-3)]',
      )}
    >
      {k.label === '⌫' ? <Delete size={16} /> : k.label}
    </button>
  )
}
